import { useEffect, useRef, useState } from 'react';
import { Menu, ChevronDown, Check } from 'lucide-react';
import type { AppTab } from './AppTabs';

/**
 * PortalNavMenu — a single pop-out menu for the Customer Portal.
 *
 * Owner directive 2026-09-30: the old icon-only tab bar was too cramped to tell
 * the apps apart. This replaces it with ONE button that opens a menu listing
 * every section as icon + full label (+ a short description), so you can read
 * what each app is before choosing it.
 */
interface PortalNavMenuProps {
  tabs: AppTab[];
  active: string;
  onChange: (key: string) => void;
  idPrefix?: string;
}

export default function PortalNavMenu({ tabs, active, onChange, idPrefix = 'portal' }: PortalNavMenuProps) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const activeTab = tabs.find(t => t.key === active);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (key: string) => { onChange(key); setOpen(false); };

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        data-agent-id={`${idPrefix}.menu`}
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
      >
        <Menu size={18} aria-hidden="true" />
        <span className="max-w-[11rem] truncate">{activeTab?.label || 'Menu'}</span>
        <ChevronDown size={16} aria-hidden="true" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Customer Portal sections"
          data-agent-id={`${idPrefix}.menu.list`}
          className="absolute left-0 top-full z-40 mt-2 max-h-[70vh] w-[min(92vw,24rem)] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        >
          {tabs.map(({ key, label, icon: Icon, desc }) => {
            const isActive = key === active;
            return (
              <button
                key={key}
                type="button"
                role="menuitem"
                aria-current={isActive ? 'true' : undefined}
                data-agent-id={`${idPrefix}.menu.item.${key}`}
                onClick={() => choose(key)}
                className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-800 dark:bg-brand-500/15 dark:text-brand-200'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                }`}
              >
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isActive ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                  <Icon size={17} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-sm font-semibold">
                    {label}
                    {isActive && <Check size={14} aria-hidden="true" />}
                  </span>
                  {desc && <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{desc}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
